import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { publishPaletteAsCssVariables } from "@/lib/designSystem";

/**
 * Branding — DSG-003 Dynamic Company Branding Service (frontend contract).
 *
 * Single source of truth for the display name, legal name, tagline, logos,
 * and brand colour palette. Any component that would otherwise import a
 * logo file, hard-code a company name, or write a hex colour MUST consume
 * this context instead.
 *
 * Fetches `/api/branding` (public — works before sign-in). Automatically
 * refreshes on demand via `refresh()` so the Administration panel can
 * force a repaint after saving.
 *
 * Bust-cache token appended to asset URLs so browsers pick up new uploads
 * without a hard reload.
 */
const DEFAULT_BRANDING = {
  identity: {
    display_name: "Coolman Fuels",
    legal_name: "Coolman Fuels Ltd.",
    short_name: "Coolman",
    tagline: null,
  },
  assets: {
    primary_logo: null,
    dark_logo: null,
    square_logo: null,
    favicon: null,
    application_icon: null,
  },
  colors: {
    brand: "#1A1A1A",
    brand_foreground: "#FFFFFF",
    brand_hover: "#000000",
    accent: "#EB1D25",
    surface: "#FFFFFF",
    surface_alt: "#F3F4F6",
  },
};

// DSG-006 · fallback host identity used when /api/applications/host
// hasn't answered yet (or fails). Prevents an empty flash on SignIn /
// Header during first paint.
const DEFAULT_HOST = {
  name: "Fuel Order Centre",
  subtitle: "Fuel Order Centre",
  department: "Operations",
  application_id: null,
};

// Platform-default fallback served from /public when no asset uploaded.
const DEFAULT_PRIMARY_LOGO = "/coolman-logo.png";

const BrandingContext = createContext({
  branding: DEFAULT_BRANDING,
  host: DEFAULT_HOST,
  loading: true,
  refresh: () => Promise.resolve(),
  primaryLogoSrc: () => DEFAULT_PRIMARY_LOGO,
});

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [host, setHost] = useState(DEFAULT_HOST);
  const [loading, setLoading] = useState(true);
  const [bust, setBust] = useState(() => Date.now());

  const load = useCallback(async () => {
    try {
      // Fetch branding + host in parallel — both are public pre-auth.
      const [b, h] = await Promise.allSettled([
        api.get("/branding"),
        api.get("/applications/host"),
      ]);
      if (b.status === "fulfilled") setBranding(b.value.data);
      if (h.status === "fulfilled") setHost({ ...DEFAULT_HOST, ...h.value.data });
    } catch {
      // Fall back to defaults — API may be unreachable pre-auth in some edge cases.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const refresh = useCallback(async () => {
    setBust(Date.now());
    await load();
  }, [load]);

  // Convenience helper: returns a usable <img src> for the primary logo,
  // preferring the uploaded asset (with cache-buster), else the default.
  const primaryLogoSrc = useCallback(() => {
    const url = branding?.assets?.primary_logo;
    return url ? `${url}?v=${bust}` : DEFAULT_PRIMARY_LOGO;
  }, [branding, bust]);

  const darkLogoSrc = useCallback(() => {
    const url = branding?.assets?.dark_logo;
    if (url) return `${url}?v=${bust}`;
    // Fall through to primary if no dark variant uploaded.
    const p = branding?.assets?.primary_logo;
    return p ? `${p}?v=${bust}` : DEFAULT_PRIMARY_LOGO;
  }, [branding, bust]);

  // Square logo — used wherever a square/badge treatment is needed
  // (loader tiles, small avatars). Falls back to primary if not set.
  const squareLogoSrc = useCallback(() => {
    const url = branding?.assets?.square_logo;
    if (url) return `${url}?v=${bust}`;
    const p = branding?.assets?.primary_logo;
    return p ? `${p}?v=${bust}` : DEFAULT_PRIMARY_LOGO;
  }, [branding, bust]);

  // Default Application Icon — used by the Application Registry / My Apps
  // tiles whenever an application has not supplied its own icon (DSG-004).
  // Falls back to square logo, then primary logo.
  const applicationIconSrc = useCallback(() => {
    const url = branding?.assets?.application_icon;
    if (url) return `${url}?v=${bust}`;
    const sq = branding?.assets?.square_logo;
    if (sq) return `${sq}?v=${bust}`;
    const p = branding?.assets?.primary_logo;
    return p ? `${p}?v=${bust}` : DEFAULT_PRIMARY_LOGO;
  }, [branding, bust]);

  // Sync <title> using host application name + company legal name.
  useEffect(() => {
    const appName = host?.name || "Fuel Order Centre";
    const org = branding?.identity?.display_name || "Coolman Fuels";
    document.title = `${appName} — ${org}`;
  }, [branding, host]);

  // DSG-006 · Publish the Brand Colour Palette to :root CSS variables
  // whenever branding refreshes. The rest of the app reads
  // `var(--color-primary)` etc. — never the hex codes directly. This is
  // the "Design System derives the theme" contract in one line.
  useEffect(() => {
    publishPaletteAsCssVariables(branding?.colors);
  }, [branding]);

  useEffect(() => {
    const faviconUrl = branding?.assets?.favicon
      ? `${branding.assets.favicon}?v=${bust}`
      : DEFAULT_PRIMARY_LOGO;
    const link = document.querySelector("link[rel='icon']");
    if (link) link.setAttribute("href", faviconUrl);
  }, [branding, bust]);

  return (
    <BrandingContext.Provider
      value={{ branding, host, loading, refresh, primaryLogoSrc, darkLogoSrc, squareLogoSrc, applicationIconSrc }}
    >
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}

/**
 * <BrandLogo /> — drop-in <img> that renders the current company logo.
 * Never reference logo files directly; use this component (DSG-003).
 */
export function BrandLogo({ variant = "primary", alt, className, ...rest }) {
  const { branding, primaryLogoSrc, darkLogoSrc } = useBranding();
  const src = variant === "dark" ? darkLogoSrc() : primaryLogoSrc();
  const fallback = alt || branding?.identity?.display_name || "Coolman";
  return <img src={src} alt={fallback} className={className} {...rest} />;
}

export { DEFAULT_PRIMARY_LOGO };
